import * as React from 'react';
import { useSelector } from 'react-redux';

import { getSubmissionDetailsLoading, getSovLocations, getUwReviewsData } from 'application/redux/selectors';
import { CircularLoader } from 'components/Loader';
import LrGrid from 'components/LrGrid';
import { UwGridRow, UwReview, UwBRLocationReview, UnderwriterReview } from 'types/underwriting.type';
import { PRODUCT_LINE_TYPES } from '../../../../constants';
import { getReviewLumenParticipationPercent } from 'helpers/brCalculationHelper';

import { columnDefs, defaultColDef } from './GridDefs';
import { Page, Spacing } from '../../../Layout';
import { LabeledData } from '../../../Input';
import { formatDollar, formatPercent } from '../../../../helpers/formatters';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { getConstructionStageString } from '../../../../application/Providers/Installation/BuildersRisk/Helpers/helpers';
import { calculateTriaPremiumFromTotalPremium } from '../../../../helpers/calculations';

interface Props {
    selectedUWReview: UwReview;
    isLoading: boolean;
}

const MY_PRODUCT_LINE_TYPE = PRODUCT_LINE_TYPES.BUILDERS_RISK;

const UwBRSummary: React.FunctionComponent<Props> = (props) => {
    const { isLoading, selectedUWReview } = props;

    // Global State
    const submissionIsLoading = useSelector(getSubmissionDetailsLoading);
    const locations = useSelector(getSovLocations);
    const uwReviews = useSelector(getUwReviewsData);

    // Local State
    const [gridData, setGridData] = React.useState([] as UwGridRow[]);
    const [brReview, setBrReview] = React.useState<UnderwriterReview | null>(null);
    const [locationReviews, setLocationReviews] = React.useState<UwBRLocationReview[]>([]);

    // Find the in-force BR underwriter review
    React.useEffect(() => {
        if (uwReviews && uwReviews.length > 0) {
            const uwReview = uwReviews.find(
                (review) => review.underwriterReview.productLineTypeCode === MY_PRODUCT_LINE_TYPE
            );
            setBrReview(uwReview?.underwriterReview || null);
            setLocationReviews(uwReview?.locationReviews || []);
        }
    }, [uwReviews]);

    const createGridData = () => {
        if (!locations.length || !selectedUWReview.locationReviews) return;

        const data = selectedUWReview.locationReviews.map(
            ({ programPremium, locationName, locationZone, totalContractValue, finalRate }: UwBRLocationReview) => {
                return {
                    locationName: locationName || '--',
                    programPremium,
                    tcv: totalContractValue,
                    zone: locationZone,
                    finalRate,
                };
            }
        );
        setGridData(data);
    };

    React.useEffect(createGridData, [selectedUWReview, locations]);

    // Calculate Deposit Premium and TRIA Premium from the underwriter review's premium field
    const premium = selectedUWReview?.underwriterReview?.premium;
    const triaPercentage = selectedUWReview?.underwriterReview?.triaPercentage || 0;

    // Premium includes TRIA, so we calculate TRIA and subtract it to get Deposit Premium
    const triaPremium =
        premium && triaPercentage > 0 ? calculateTriaPremiumFromTotalPremium(premium, triaPercentage) : 0;

    const depositPremium = premium ? premium - triaPremium : null;

    if (isLoading || submissionIsLoading) return <CircularLoader />;

    return (
        <Page title={'BR Underwriting Summary'}>
            <Grid container spacing={3}>
                {brReview && (
                    <>
                        <Grid>
                            <LabeledData title={'Policy Type'} value={brReview?.isLeader ? 'Leader' : 'Follower'} />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'Lumen Participation %'}
                                value={
                                    brReview?.policyLimitOfLiability && brReview?.lumenLimitOfLiability
                                        ? `${getReviewLumenParticipationPercent(
                                              brReview.policyLimitOfLiability,
                                              brReview.lumenLimitOfLiability
                                          )}%`
                                        : '--'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'Construction Stage'}
                                value={getConstructionStageString(locationReviews)}
                            />
                        </Grid>
                    </>
                )}
                <Grid>
                    <LabeledData
                        title={'Agent Commission'}
                        value={
                            formatPercent(selectedUWReview?.underwriterReview?.brokerCommissionPercentage, 1) || '--'
                        }
                    />
                </Grid>
                <Grid>
                    <LabeledData
                        title={'Deposit Premium'}
                        value={depositPremium !== null ? formatDollar(depositPremium) : '--'}
                    />
                </Grid>
                <Grid>
                    <LabeledData
                        title={'TRIA Premium'}
                        value={triaPremium !== null ? formatDollar(triaPremium) : '--'}
                    />
                </Grid>
                <Grid>
                    <LabeledData
                        title={'Policy Fee'}
                        value={formatDollar(selectedUWReview?.underwriterReview?.policyFee) || '--'}
                    />
                </Grid>
                <Grid>
                    <LabeledData
                        title={'Professional Fee'}
                        value={formatDollar(selectedUWReview?.underwriterReview?.professionalFee) || '--'}
                    />
                </Grid>
                <Grid>
                    <LabeledData
                        title={'Inspection Fee'}
                        value={formatDollar(selectedUWReview?.underwriterReview?.inspectionFee) || '--'}
                    />
                </Grid>
            </Grid>
            <Spacing height={'24px'} />
            <Typography variant={'h5'}>Location Reviews</Typography>
            <LrGrid
                defaultColDef={defaultColDef}
                columnDefs={columnDefs}
                rowData={gridData}
                height={'25vh'}
                // domLayout={'autoHeight'}
                sizeToFit
                autosize
            />
        </Page>
    );
};

export default React.memo(UwBRSummary);
